import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { ErrorResponseHandlerService } from "@nms-ng2/app/core/services/error-response-handler/error-response-handler.service";
import { catchError } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { DwdmCircuitData, DwdmCircuitEndsData } from "../general-step.model";
import { ErrorResponse, NmsError } from "@nms-ng2/app/core/services/error-response-handler/error-response-handler.model";
import { ERROR_NAME } from "@nms-ng2/app/core/services/error-response-handler/error-names.constant";
import { TranslationHelperService } from "@nms-ng2/app/shared/services/util/translation-helper.service";

@Injectable({
    providedIn: "root"
})

/**
 * Serviço responsável pelas operações relacionadas ao passo Geral do DWDM Circuit Wizard.
 */
export class GeneralStepService {
    private readonly baseUrl = "/web/topology/circuit/dwdm";

    constructor(private readonly http: HttpClient, private readonly errorResponseHandlerService: ErrorResponseHandlerService,
        private readonly nmsDialogService: NmsDialogService, private readonly translationHelper: TranslationHelperService) { }

    generateCircuitName(endsData: DwdmCircuitEndsData): Observable<string> {
        return this.http
            .post(`${this.baseUrl}/options/generate-name`, endsData, {
                responseType: "text"
            })
            .pipe(
                catchError((error) => {
                    this.handleError(error);
                    return of("");
                })
            );
    }

    createAndActivate(dwdmCircuitData: DwdmCircuitData): Observable<Object> {
        return this.http
            .post(`${this.baseUrl}/create-and-activate`, dwdmCircuitData)
            .pipe(
                catchError((error) => {
                    this.handleError(error);
                    return of({});
                })
            );
    }

    updateAndActivate(dwdmCircuitData: DwdmCircuitData): Observable<Object> {
        return this.http
            .put(`${this.baseUrl}/update-and-activate`, dwdmCircuitData)
            .pipe(
                catchError((error) => {
                    this.handleError(error);
                    return of({});
                })
            );
    }

    private handleError(error: HttpErrorResponse) {
        const { code } = error.error;

        switch (code) {
            case NmsError.DWDM_CIRCUIT_CHANNEL_USED_BY_MUX_ERROR:
            case NmsError.DWDM_CIRCUIT_INVALID_MUX_CHANNEL_RANGE:
            case NmsError.DWDM_CIRCUIT_CHANNEL_IN_USE_BY_PORT_ERROR:
            case NmsError.DWDM_CIRCUIT_INTERMEDIATE_WITHOUT_EDFA_PORT:
            case NmsError.DWDM_CIRCUIT_INTERMEDIATES_MUST_HAVE_EDFA_PORT:
            case NmsError.DWDM_CIRCUIT_ENDS_TX_MUST_HAVE_A_TX_OSC:
            case NmsError.DWDM_CIRCUIT_ENDS_RX_MUST_HAVE_A_RX_OSC:
            case NmsError.DWDM_CIRCUIT_MULTIPLE_OSC_MANAGEMENT_PROFILES:
                this.handleErrorMessageWithMultipleDetailsParameters(error.error);
                break;
            default:
                this. handleErrorWithNoDetails(error);
        }
    }

    /**
     * Mensagem customizada para os errorCodes
     * no qual necessita de parâmetros que vêm detalhados na exceção de acordo com
     * o erro ocorrido.
     * @param errorResponse detalhes do erro ocorrido
     */
    private handleErrorMessageWithMultipleDetailsParameters(errorResponse: ErrorResponse): void {
        const { code, details } = errorResponse;
        const errorName = ERROR_NAME[code];

        if (!errorName) {
            const unknownError = this.translationHelper.translateByPrefix("response.error.", "UNEXPECTED_ERROR");
            throw new Error(`${unknownError} ${code}`);
        }

        const description = this.translationHelper.translateWithReplacement(`response.error.${errorName}`, ...details);
        this.openErrorDialog(description);
    }

    private handleErrorWithNoDetails(error: HttpErrorResponse): void {
        const { description, details } = this.errorResponseHandlerService.buildErrorDescriptionDetails(error.error);
        this.openErrorDialog(description + details);
    }

    private openErrorDialog(description: string) {
        this.nmsDialogService.openDialog(
            { description, acceptLabel: "OK", bindHtml: true },
            { maxWidth: "800px" }
        );
    }
}
